/**
 * Orders Table Component
 * 
 * This component displays a table of orders with
 * filtering, sorting, and action capabilities.
 */

import React from 'react';
import { useApp } from '../../context/AppContext';
import { useTranslations } from '../../context/TranslationsContext';

const OrdersTable = ({ orders, onViewDetails, onApprove, onUpdateStatus }) => {
  const { translations } = useTranslations();
  const { state } = useApp();
  const t = translations[state.currentLanguage];

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US');
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return t.notSpecified;
    return new Intl.NumberFormat(state.currentLanguage === 'ar' ? 'ar-SA' : 'en-US').format(price);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      rejected: 'bg-red-100 text-red-800',
      active: 'bg-green-100 text-green-800',
      in_warehouse: 'bg-blue-100 text-blue-800',
      delivering_to_customer: 'bg-purple-100 text-purple-800',
      delivering_to_warehouse: 'bg-orange-100 text-orange-800',
      completed: 'bg-gray-100 text-gray-800',
      needs_attention: 'bg-red-100 text-red-800',
      retrieval_requested: 'bg-orange-100 text-orange-800',
      retrieval_approved: 'bg-green-100 text-green-800',
      retrieval_delivering: 'bg-purple-100 text-purple-800',
      retrieval_completed: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="table-container">
      <table className="data-table">
        <thead>
          <tr>
            <th>{t.orderNumber}</th>
            <th>{t.customerName}</th>
            <th>{t.phone}</th>
            <th>{t.space}</th>
            <th>{t.duration}</th>
            <th>{t.price}</th>
            <th>{t.status}</th>
            <th>{t.createdAt}</th>
            <th>{t.actions}</th>
          </tr>
        </thead>
        <tbody>
          {orders.map(order => (
            <tr key={order.id}>
              <td>#{order.id.substring(0, 8)}</td>
              <td>{order.customer_name}</td>
              <td>{order.customer_phone}</td>
              <td>{order.space_required} {t.sqm}</td>
              <td>{order.duration_months} {t.months}</td>
              <td>{formatPrice(order.total_price)} {order.total_price ? t.sar : ''}</td>
              <td>
                <span className={`status-badge ${getStatusColor(order.status)}`}>
                  {t[order.status]}
                </span>
              </td>
              <td>{formatDate(order.created_at)}</td>
              <td>
                <div className="action-buttons">
                  <button
                    onClick={() => onViewDetails(order)}
                    className="btn-secondary btn-sm"
                  >
                    {t.viewDetails}
                  </button>
                  {order.status === 'pending' && (
                    <>
                      <button
                        onClick={() => onApprove(order.id, 'approved')}
                        className="btn-success btn-sm"
                      >
                        {t.approve}
                      </button>
                      <button
                        onClick={() => onApprove(order.id, 'rejected')}
                        className="btn-danger btn-sm"
                      >
                        {t.reject}
                      </button>
                    </>
                  )}
                  
                  {/* Status Update Button for all orders */}
                  <button
                    onClick={() => onUpdateStatus(order)}
                    className="btn-primary btn-sm"
                  >
                    {t.updateStatus}
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default OrdersTable;
